import React, { useState, useMemo, useEffect } from 'react';
import useGameBase from '../../hooks/useGameBase';

function Square({ piece, onClick }) {
  const imgSrc = piece ? `/icons/chess/${piece}.svg` : null;
  return (
    <div onClick={onClick} className={`flex h-full w-full items-center justify-center ${piece ? 'cursor-pointer hover:bg-gray-500 dark:hover:bg-gray-500' : ''}`}> 
      {piece ? (
        <img src={imgSrc} alt={piece} className="h-[72%] w-[72%] select-none pointer-events-none" />
      ) : null}
    </div>
  );
}

export default function ChessGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const idCandidates = useMemo(() => [user?.socketId, user?.userId, user?.id].filter(Boolean), [user]);
  const localPid = idCandidates[0] || null;
  const board = gameState.board || Array.from({length:8}, ()=>Array(8).fill(null));
  const [selected, setSelected] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [promotionChoice, setPromotionChoice] = useState(null);
  const [invalidAttempt, setInvalidAttempt] = useState(null);

  useEffect(() => {
    if (!socket || !roomId) return;
    const handler = ({ roomId: rid, moves }) => {
      if (rid !== roomId) return;
      setLegalMoves(moves || []);
    };
    socket.on('requestLegalMovesResult', handler);
    return () => socket.off('requestLegalMovesResult', handler);
  }, [socket, roomId]);

  const handleSquareClick = (r,c) => {
    const piece = board[r][c];
    if (selected) {
      // attempt move
      const [fr,fc] = selected;
      // check if this destination is a legal move according to server-provided list
      const candidate = (legalMoves || []).find(m => m.from[0]===fr && m.from[1]===fc && m.to[0]===r && m.to[1]===c);
      if (!candidate) {
        // Highlight invalid attempt regardless of check status
        setInvalidAttempt([r,c]);
        setTimeout(()=> setInvalidAttempt(null), 800);
        setSelected(null);
        setLegalMoves([]);
        return;
      }
      // if this is a pawn promotion, ask choice
      const pieceAtFrom = board[fr][fc];
      const isPawn = pieceAtFrom && pieceAtFrom[1]==='P';
      const lastRank = pieceAtFrom && pieceAtFrom[0]==='w' ? 0 : 7;
      if (isPawn && r===lastRank) {
        setPromotionChoice({ from: [fr,fc], to: [r,c], candidate });
        return;
      }
      submitAction && submitAction({ type: 'move', from: [fr,fc], to: [r,c] }).catch(()=>{});
      setSelected(null);
      setLegalMoves([]);
      return;
    }
    // pick up only if piece belongs to local player color (simple heuristic: first player = white)
  if (!piece) return setSelected(null);
    const isWhite = piece.startsWith('w');
    const firstPlayer = gamePlayers && gamePlayers.length>0 ? gamePlayers[0] : null;
    const amFirst = firstPlayer && localPid && (localPid === firstPlayer.socketId || localPid === firstPlayer.userId);
    // allow selecting if colors match or if not enough context, allow anyway
    if ((amFirst && isWhite) || (!amFirst && !isWhite) || (gamePlayers.length < 2)) {
      setSelected([r,c]);
      // request legal moves for this player from server
      try { socket && socket.emit('requestLegalMoves', { roomId, forPlayerId: localPid || socket.id }); } catch (e) {}
    }
  };

  // --- client-side helpers to detect check (used only for UI highlighting) ---
  const inBounds = (r,c) => r>=0 && r<8 && c>=0 && c<8;
  const pieceColor = (p) => { if(!p) return null; return p.startsWith('w') ? 'w' : 'b'; };
  const pieceType = (p) => { if(!p) return null; return p.substring(1); };

  const isSquareAttacked = (bd, tr, tc, byColor) => {
    // pawns
    const pawnDir = byColor === 'w' ? -1 : 1;
    for (const dc of [-1,1]) {
      const r = tr + pawnDir, c = tc + dc;
      if (inBounds(r,c) && bd[r][c] && pieceColor(bd[r][c]) === byColor && pieceType(bd[r][c]) === 'P') return true;
    }
    // knights
    const knights = [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]];
    for (const [dr,dc] of knights) {
      const r = tr+dr, c = tc+dc;
      if (inBounds(r,c) && bd[r][c] && pieceColor(bd[r][c])===byColor && pieceType(bd[r][c])==='N') return true;
    }
    // sliding: bishops/rooks/queens
    const slideDirs = [ [-1,0],[1,0],[0,-1],[0,1],[-1,-1],[-1,1],[1,-1],[1,1] ];
    for (const [dr,dc] of slideDirs) {
      let r = tr+dr, c = tc+dc;
      while (inBounds(r,c)) {
        const p = bd[r][c];
        if (p) {
          if (pieceColor(p) === byColor) {
            const t = pieceType(p);
            if ((Math.abs(dr)===1 && Math.abs(dc)===1) && (t==='B' || t==='Q')) return true;
            if ((dr===0 || dc===0) && (t==='R' || t==='Q')) return true;
            // adjacent king (in case)
            if (Math.abs(r-tr)<=1 && Math.abs(c-tc)<=1 && t==='K') return true;
          }
          break;
        }
        r += dr; c += dc;
      }
    }
    // king adjacency
    for (let dr=-1; dr<=1; dr++) for (let dc=-1; dc<=1; dc++){
      if (dr===0 && dc===0) continue;
      const r = tr+dr, c = tc+dc;
      if (inBounds(r,c) && bd[r][c] && pieceColor(bd[r][c])===byColor && pieceType(bd[r][c])==='K') return true;
    }
    return false;
  };

  // helper: find the king position for a given color
  const findKingPosition = (bd, color) => {
    for (let r=0;r<8;r++) for (let c=0;c<8;c++){
      const p = bd[r][c];
      if (p && pieceType(p)==='K' && pieceColor(p)===color) return [r,c];
    }
    return null;
  };

  // compute which king squares (if any) are currently in check so we can highlight only those
  const kingInCheckPositions = (() => {
    try {
      const res = [];
      const wPos = findKingPosition(board, 'w');
      if (wPos) {
        if (isSquareAttacked(board, wPos[0], wPos[1], 'b')) res.push(wPos);
      }
      const bPos = findKingPosition(board, 'b');
      if (bPos) {
        if (isSquareAttacked(board, bPos[0], bPos[1], 'w')) res.push(bPos);
      }
      return res;
    } catch (e) { return []; }
  })();

  // Legend component: moved out of effect so it actually mounts and renders
  function Legend({ gamePlayers = [], gameState = {} }){
    const white = gamePlayers && gamePlayers.length>0 ? gamePlayers[0] : null;
    const black = gamePlayers && gamePlayers.length>1 ? gamePlayers[1] : null;
    const scores = useMemo(()=> Object.fromEntries((gameState.players||[]).map(p=>[p.socketId, p.score||0])), [gameState.players]);
    return (
      <div className="mt-3 w-full">
        <div className="border rounded-md bg-gray-500 dark:bg-gray-500 p-2">
          <div className="text-sm font-semibold mb-1 text-gray-100 dark:text-gray-100">Players</div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/icons/chess/wP.svg" alt="White" className="w-8 h-8" />
              <div>
                <div className="font-medium text-gray-100 dark:text-gray-100">{white ? white.username : 'White (waiting...)'}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{white && scores[white.socketId] != null ? `${scores[white.socketId]} pts` : ''}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="font-medium text-gray-100 dark:text-gray-100">{black ? black.username : 'Black (waiting...)'}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{black && scores[black.socketId] != null ? `${scores[black.socketId]} pts` : ''}</div>
              </div>
              <img src="/icons/chess/bP.svg" alt="Black" className="w-8 h-8" />
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-2">
      <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-stone-100 via-amber-50 to-orange-100 p-2 shadow-sm dark:border-amber-900/70 dark:from-gray-900 dark:via-gray-800 dark:to-stone-900 sm:p-3">
      <div className="mx-auto grid aspect-square w-full max-w-[min(92vw,44rem)] grid-cols-8 gap-0 overflow-hidden rounded-2xl border border-gray-700 shadow-inner dark:border-gray-900">
        {board.map((row, r) => row.map((p, c) => {
          const isDest = (legalMoves || []).some(m => m.to[0]===r && m.to[1]===c && selected && m.from[0]===selected[0] && m.from[1]===selected[1]);
          const isSel = selected && selected[0]===r && selected[1]===c;
          const isKingInCheckCell = kingInCheckPositions.some(pos => pos[0]===r && pos[1]===c);
          const cellClass = `relative aspect-square ${isSel ? 'bg-blue-500' : ''}`;
          // draw thin single borders by painting only top and left; paint right/bottom on edges
          const borderColor = 'rgba(0,0,0,0.18)';
          const style = { backgroundColor: ((r+c)%2===0) ? '#af833cff':'#6b3203ff', borderTop: `0.6px solid ${borderColor}`, borderLeft: `0.6px solid ${borderColor}` };
          // if (r === 7) style.borderBottom = `0.6px solid ${borderColor}`;
          // if (c === 7) style.borderRight = `0.6px solid ${borderColor}`;

          return (
          <div key={`${r}-${c}`} className={cellClass} style={style}>
            <div className="w-full h-full flex items-center justify-center" onClick={() => handleSquareClick(r,c)}>
              <Square piece={p} onClick={() => {}} />
            </div>
            {isDest && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="w-3 h-3 rounded-full bg-blue-500 opacity-80"></div></div>}
            {/* {animMove && animMove.to && animMove.to[0]===r && animMove.to[1]===c && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-8 h-8 rounded bg-yellow-300 dark:bg-yellow-500 opacity-90 transform transition ease-out duration-500 animate-scale-up" />
              </div>
            )} */}
            {invalidAttempt && invalidAttempt[0]===r && invalidAttempt[1]===c && (
              <div className="absolute inset-0 pointer-events-none">
                <div className="w-full h-full border-2 border-red-500 opacity-90" />
              </div>
            )}
            {isKingInCheckCell && (
              <div className="absolute inset-0 pointer-events-none"><div className="w-full h-full bg-red-400 opacity-50" /></div>
            )}
          </div>
        )}))}
      </div>
      </div>
      <div className="mx-auto w-full max-w-[min(92vw,44rem)]">
        <Legend gamePlayers={gamePlayers} gameState={gameState} />
      </div>

      {gameState && gameState.result && gameState.result.reason === 'checkmate' && (
        <div className="mt-2 text-sm font-bold text-red-600">Checkmate</div>
      )}

      {/* Promotion modal (icon-based) */}
      {promotionChoice && (() => {
        const { from, to } = promotionChoice;
        const pieceFrom = board[from[0]] ? board[from[0]][from[1]] : null;
        const color = pieceFrom ? pieceFrom[0] : 'w';
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 p-4 rounded shadow-lg transform transition ease-out duration-200 animate-scale-up">
              <div className="mb-2 font-semibold text-gray-800 dark:text-gray-100">Choose promotion</div>
              <div className="flex gap-3">
                {['Q','R','B','N'].map((p) => {
                  const code = `${color}${p}`;
                  const src = `/icons/chess/${code}.svg`;
                  return (
                    <button title={`Promote to ${p}`} key={p} className="p-1 bg-gray-800 dark:bg-gray-700 rounded hover:scale-110 transform transition" onClick={() => {
                      submitAction && submitAction({ type: 'move', from, to, promotion: p }).catch(() => {});
                      setPromotionChoice(null);
                      setSelected(null);
                      setLegalMoves([]);
                    }}>
                      <img src={src} alt={p} className="w-8 h-8" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
      
    </div>
  );
}

ChessGame.Guide = function ChessGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to play Chess</h3>
      <p className="mb-1">A classic two-player strategy board game played on an 8x8 grid.</p>
      <p className="mb-1">The objective is to checkmate the opponent's king.</p>
      <p className="mb-1">Each type of piece has its own unique movement patterns:</p>
      <table className="mb-2 w-full text-left border-separate" style={{ borderSpacing: '0 0.5rem' }}>
        <tbody>
          <tr>
            <td className="py-2 pr-3 align-top"><img src="/icons/chess/wP.svg" alt="Pawn" className="w-16 h-16 inline" /></td>
            <td className="py-2 align-top"><b>Pawns</b>: Move forward one square (or two from their starting position). Capture diagonally. Can promote to another piece upon reaching the far rank. En passant and promotion rules apply.</td>
          </tr>
          <tr>
            <td className="py-2 pr-3 align-top"><img src="/icons/chess/wR.svg" alt="Rook" className="w-16 h-16 inline" /></td>
            <td className="py-2 align-top"><b>Rooks</b>: Move any number of squares horizontally or vertically.</td>
          </tr>
          <tr>
            <td className="py-2 pr-3 align-top"><img src="/icons/chess/wB.svg" alt="Bishop" className="w-16 h-16 inline" /></td>
            <td className="py-2 align-top"><b>Bishops</b>: Move any number of squares diagonally.</td>
          </tr>
          <tr>
            <td className="py-2 pr-3 align-top"><img src="/icons/chess/wN.svg" alt="Knight" className="w-16 h-16 inline" /></td>
            <td className="py-2 align-top"><b>Knights</b>: Move in an L-shape: two squares in one direction, then one square perpendicular. Knights can jump over other pieces.</td>
          </tr>
          <tr>
            <td className="py-2 pr-3 align-top"><img src="/icons/chess/wQ.svg" alt="Queen" className="w-16 h-16 inline" /></td>
            <td className="py-2 align-top"><b>Queen</b>: Combines the power of the rook and bishop, moving any number of squares in any direction.</td>
          </tr>
          <tr>
            <td className="py-2 pr-3 align-top"><img src="/icons/chess/wK.svg" alt="King" className="w-16 h-16 inline" /></td>
            <td className="py-2 align-top"><b>King</b>: Moves one square in any direction. The king cannot move into check. Castling is a special move involving the king and a rook.</td>
          </tr>
        </tbody>
      </table>
      <p className="mb-1">Players take turns moving their pieces, with the goal of outmaneuvering their opponent.</p>
      <p className="mb-1">The game ends when one player's king is checkmated.</p>
      <p className="mb-1">Chess is a game of skill, strategy, and foresight.</p>
      <p className="mb-1">Understanding the strengths and weaknesses of each piece is key to success.</p>
    </div>
  );
};
